/**
 * Admin Boost Analytics Dashboard
 * Shows conversion metrics for the boost popup feature
 * Includes A/B test results comparison
 */
import React, { useState, useEffect, useCallback } from 'react';
import { 
  Rocket, Eye, Zap, X, Loader2, RefreshCw, TrendingUp, 
  ArrowUp, ArrowDown, Percent, Calendar, FlaskConical, Trophy, Crown
} from 'lucide-react';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, 
  Tooltip, Legend, ResponsiveContainer, BarChart, Bar 
} from 'recharts';
import { useAdmin } from './AdminContext';

const StatCard = ({ title, value, subtitle, icon: Icon, color = 'blue', trend }) => (
  <div className="bg-card border border-border rounded-xl p-5 hover:shadow-lg transition-shadow">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-sm text-muted-foreground mb-1">{title}</p>
        <p className="text-3xl font-bold">{value}</p>
        {subtitle && (
          <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>
        )}
        {trend !== undefined && (
          <div className={`flex items-center gap-1 text-sm mt-2 ${trend >= 0 ? 'text-green-500' : 'text-red-500'}`}>
            {trend >= 0 ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
            {Math.abs(trend)}% vs previous period
          </div>
        )}
      </div>
      <div className={`p-3 rounded-xl bg-${color}-500/10`}>
        <Icon className={`w-6 h-6 text-${color}-500`} />
      </div>
    </div>
  </div>
);

const AdminBoostAnalytics = () => {
  const { apiCall } = useAdmin();
  
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  
  const fetchAnalytics = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiCall('get', `/api/admin/analytics/boost-popup?days=${days}`);
      setData(response);
    } catch (error) {
      console.error('Failed to fetch boost analytics:', error);
    } finally {
      setLoading(false);
    }
  }, [apiCall, days]);
  
  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);
  
  // Format numbers
  const formatNumber = (num) => {
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num?.toString() || '0';
  };

  // Calculate funnel percentages
  const getFunnelData = () => {
    if (!data?.summary) return [];
    const { total_views, total_clicks, total_dismisses } = data.summary;
    return [
      { name: 'Popup Views', value: total_views, fill: '#3b82f6' },
      { name: 'Boost Clicks', value: total_clicks, fill: '#22c55e' },
      { name: 'Dismisses', value: total_dismisses, fill: '#ef4444' },
    ];
  };
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Rocket className="w-6 h-6 text-primary" />
            Boost Popup Analytics
          </h2>
          <p className="text-sm text-muted-foreground">
            Track how users interact with the post-publish boost popup
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Period selector */}
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="px-3 py-2 bg-secondary rounded-lg text-sm border-0 focus:ring-2 focus:ring-primary"
          >
            <option value={7}>Last 7 days</option>
            <option value={14}>Last 14 days</option>
            <option value={30}>Last 30 days</option>
            <option value={60}>Last 60 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          
          <button
            onClick={fetchAnalytics}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-secondary rounded-lg hover:bg-secondary/80 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>
      
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : data ? (
        <>
          {/* Summary Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              title="Popup Views"
              value={formatNumber(data.summary.total_views)}
              subtitle={`${days} day period`}
              icon={Eye}
              color="blue"
            />
            <StatCard
              title="Boost Clicks"
              value={formatNumber(data.summary.total_clicks)}
              subtitle="Users who clicked boost"
              icon={Zap}
              color="green"
            />
            <StatCard
              title="Dismisses"
              value={formatNumber(data.summary.total_dismisses)}
              subtitle="Users who clicked later"
              icon={X}
              color="red"
            />
            <StatCard
              title="Conversion Rate"
              value={`${data.summary.conversion_rate}%`}
              subtitle="Clicks / Views"
              icon={Percent}
              color="purple"
            />
          </div>

          {/* Conversion Funnel Visual */}
          <div className="bg-card border border-border rounded-xl p-5">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-primary" />
              Conversion Funnel
            </h3>
            <div className="flex items-end justify-around h-48 gap-4">
              {getFunnelData().map((item, idx) => {
                const maxValue = Math.max(...getFunnelData().map(i => i.value)) || 1;
                const heightPercent = (item.value / maxValue) * 100;
                return (
                  <div key={item.name} className="flex flex-col items-center flex-1">
                    <div className="w-full max-w-[100px] flex flex-col items-center">
                      <span className="text-lg font-bold mb-2">{formatNumber(item.value)}</span>
                      <div 
                        className="w-full rounded-t-lg transition-all duration-500"
                        style={{ 
                          height: `${Math.max(heightPercent, 10)}%`,
                          backgroundColor: item.fill,
                          minHeight: '20px'
                        }}
                      />
                    </div>
                    <span className="text-xs text-muted-foreground mt-2 text-center">{item.name}</span>
                  </div>
                );
              })}
            </div>
            {data.summary.total_views > 0 && (
              <div className="mt-4 p-3 bg-secondary/50 rounded-lg text-center">
                <span className="text-sm text-muted-foreground">
                  <strong className="text-green-500">{data.summary.conversion_rate}%</strong> of users who see the popup click "Boost My Listing"
                </span>
              </div>
            )}
          </div>

          {/* A/B Test Results */}
          {data.ab_test && data.ab_test.variants && (
            <div className="bg-card border border-border rounded-xl p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold flex items-center gap-2">
                  <FlaskConical className="w-5 h-5 text-purple-500" />
                  A/B Test Results
                </h3>
                {data.ab_test.is_significant ? (
                  <span className="px-2 py-1 bg-green-500/10 text-green-500 text-xs font-medium rounded-full">
                    Statistically Significant
                  </span>
                ) : (
                  <span className="px-2 py-1 bg-yellow-500/10 text-yellow-500 text-xs font-medium rounded-full">
                    Gathering Data...
                  </span>
                )}
              </div>
              
              {/* Variant comparison table */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-3 px-2 font-medium text-muted-foreground">Variant</th>
                      <th className="text-center py-3 px-2 font-medium text-muted-foreground">Views</th>
                      <th className="text-center py-3 px-2 font-medium text-muted-foreground">Clicks</th>
                      <th className="text-center py-3 px-2 font-medium text-muted-foreground">Dismisses</th>
                      <th className="text-center py-3 px-2 font-medium text-muted-foreground">Conversion</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.ab_test.variants.map((variant) => {
                      const isLeading = variant.variant_id === data.ab_test.leading_variant;
                      const variantColors = {
                        'A': 'bg-blue-500',
                        'B': 'bg-orange-500',
                        'C': 'bg-green-500'
                      };
                      return (
                        <tr 
                          key={variant.variant_id} 
                          className={`border-b border-border/50 ${isLeading ? 'bg-green-500/5' : ''}`}
                        >
                          <td className="py-3 px-2">
                            <div className="flex items-center gap-2">
                              <div className={`w-3 h-3 rounded-full ${variantColors[variant.variant_id]}`} />
                              <div>
                                <div className="flex items-center gap-2">
                                  <span className="font-medium">{variant.name}</span>
                                  <span className="text-xs text-muted-foreground">({variant.variant_id})</span>
                                  {isLeading && (
                                    <Crown className="w-4 h-4 text-yellow-500" />
                                  )}
                                </div>
                                <span className="text-xs text-muted-foreground">{variant.description}</span>
                              </div>
                            </div>
                          </td>
                          <td className="text-center py-3 px-2">{variant.views}</td>
                          <td className="text-center py-3 px-2 text-green-500 font-medium">{variant.clicks}</td>
                          <td className="text-center py-3 px-2 text-red-400">{variant.dismisses}</td>
                          <td className="text-center py-3 px-2">
                            <span className={`font-bold ${isLeading ? 'text-green-500' : ''}`}>
                              {variant.conversion_rate}%
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Conversion rate comparison bar chart */}
              <div className="mt-6">
                <h4 className="text-sm font-medium mb-3 text-muted-foreground">Conversion Rate Comparison</h4>
                <div className="space-y-3">
                  {data.ab_test.variants.map((variant) => {
                    const maxRate = Math.max(...data.ab_test.variants.map(v => v.conversion_rate)) || 1;
                    const widthPercent = (variant.conversion_rate / maxRate) * 100;
                    const variantColors = {
                      'A': 'bg-blue-500',
                      'B': 'bg-orange-500',
                      'C': 'bg-green-500'
                    };
                    const isLeading = variant.variant_id === data.ab_test.leading_variant;
                    return (
                      <div key={variant.variant_id} className="flex items-center gap-3">
                        <span className="w-24 text-sm text-muted-foreground">{variant.name}</span>
                        <div className="flex-1 bg-secondary rounded-full h-6 overflow-hidden">
                          <div 
                            className={`h-full ${variantColors[variant.variant_id]} rounded-full transition-all duration-500 flex items-center justify-end pr-2`}
                            style={{ width: `${Math.max(widthPercent, 5)}%` }}
                          >
                            <span className="text-xs text-white font-bold">
                              {variant.conversion_rate}%
                            </span>
                          </div>
                        </div>
                        {isLeading && variant.conversion_rate > 0 && (
                          <Trophy className="w-5 h-5 text-yellow-500" />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Insight box */}
              {data.ab_test.leading_variant && data.ab_test.is_significant && (
                <div className="mt-4 p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                  <div className="flex items-start gap-2">
                    <Trophy className="w-5 h-5 text-green-500 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-green-500">
                        Variant {data.ab_test.leading_variant} ({data.ab_test.variants.find(v => v.variant_id === data.ab_test.leading_variant)?.name}) is winning!
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Consider rolling out this variant to all users for better conversions.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Daily Chart */}
          {data.chart_data && data.chart_data.length > 0 && (
            <div className="bg-card border border-border rounded-xl p-5">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-primary" />
                Daily Breakdown
              </h3>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={data.chart_data}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                  <XAxis 
                    dataKey="date" 
                    className="text-xs"
                    tickFormatter={(date) => {
                      const d = new Date(date);
                      return `${d.getMonth() + 1}/${d.getDate()}`;
                    }}
                  />
                  <YAxis className="text-xs" />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))', 
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }}
                    labelFormatter={(date) => new Date(date).toLocaleDateString()}
                  />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="views" 
                    name="Views"
                    stroke="#3b82f6" 
                    strokeWidth={2}
                    dot={false}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="clicks" 
                    name="Clicks"
                    stroke="#22c55e" 
                    strokeWidth={2}
                    dot={false}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="dismisses" 
                    name="Dismisses"
                    stroke="#ef4444" 
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Recent Activity */}
          {data.recent_events && data.recent_events.length > 0 && (
            <div className="bg-card border border-border rounded-xl p-5">
              <h3 className="font-semibold mb-4">Recent Activity</h3>
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {data.recent_events.slice(0, 20).map((event, idx) => (
                  <div 
                    key={event.id || idx}
                    className="flex items-center gap-3 p-3 bg-secondary/30 rounded-lg text-sm"
                  >
                    <div className={`p-2 rounded-lg ${
                      event.event_type === 'popup_view' ? 'bg-blue-500/10 text-blue-500' :
                      event.event_type === 'boost_click' ? 'bg-green-500/10 text-green-500' :
                      'bg-red-500/10 text-red-500'
                    }`}>
                      {event.event_type === 'popup_view' ? <Eye className="w-4 h-4" /> :
                       event.event_type === 'boost_click' ? <Zap className="w-4 h-4" /> :
                       <X className="w-4 h-4" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">
                        {event.event_type === 'popup_view' ? 'Viewed popup' :
                         event.event_type === 'boost_click' ? 'Clicked Boost' :
                         'Dismissed popup'}
                      </div>
                      <div className="text-xs text-muted-foreground truncate">
                        {event.listing_title || 'Unknown listing'} • {event.user_name || 'Unknown user'}
                      </div>
                    </div>
                    <div className="text-xs text-muted-foreground whitespace-nowrap">
                      {new Date(event.created_at).toLocaleString()}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Empty state */}
          {data.summary.total_views === 0 && (
            <div className="bg-card border border-border rounded-xl p-10 text-center">
              <Rocket className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-semibold text-lg mb-2">No data yet</h3>
              <p className="text-muted-foreground text-sm">
                Boost popup analytics will appear here once users start publishing listings.
              </p>
            </div>
          )}
        </>
      ) : (
        <div className="bg-card border border-border rounded-xl p-10 text-center">
          <p className="text-muted-foreground">Failed to load analytics data</p>
        </div>
      )}
    </div>
  );
};

export default AdminBoostAnalytics;
